import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import {
  amlApi,
  type AmlAlert,
  type AmlAlertAging,
  type AmlAlertDetail,
  type AmlAlertSeverity,
  type AmlAlertStatus,
  type AmlAlertType,
} from '../../api/aml.api.js';
import { useAuth } from '../../auth/useAuth.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import { APP_LOCALE } from '../../utils/locale.js';

type ActionKind = 'assign' | 'dismiss' | 'escalate';

function agingColor(aging: AmlAlertAging): 'success' | 'warning' | 'error' {
  if (aging === 'fresh') return 'success';
  if (aging === 'warning') return 'warning';
  return 'error';
}

function severityColor(
  s: AmlAlertSeverity,
): 'default' | 'info' | 'warning' | 'error' {
  if (s === 'LOW') return 'default';
  if (s === 'MEDIUM') return 'info';
  if (s === 'HIGH') return 'warning';
  return 'error';
}

const STATUS_OPTIONS: AmlAlertStatus[] = [
  'OPEN',
  'UNDER_REVIEW',
  'ESCALATED',
  'DISMISSED',
  'SAR_FILED',
];
const SEVERITY_OPTIONS: AmlAlertSeverity[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const TYPE_OPTIONS: AmlAlertType[] = [
  'SANCTIONS_HIT',
  'TMS_RULE_TRIGGERED',
  'FRAUD_HIGH_SCORE',
  'UNMATCHED_CLEARING',
];

export function AmlAlertQueuePage(): JSX.Element {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  // DEV-67: the client manager and finance read the alerts; AML / MLRO works them.
  const isCompliance = can('aml.work');

  const [statusFilter, setStatusFilter] = useState<AmlAlertStatus | ''>('');
  const [severityFilter, setSeverityFilter] = useState<AmlAlertSeverity | ''>('');
  const [typeFilter, setTypeFilter] = useState<AmlAlertType | ''>('');
  const [selected, setSelected] = useState<AmlAlert | null>(null);

  const [actionKind, setActionKind] = useState<ActionKind | null>(null);
  const [assigneeId, setAssigneeId] = useState('');
  const [dismissReason, setDismissReason] = useState('');
  const [narrative, setNarrative] = useState('');

  const queryFilters = useMemo(
    () => ({
      ...(statusFilter !== '' ? { status: statusFilter } : {}),
      ...(severityFilter !== '' ? { severity: severityFilter } : {}),
      ...(typeFilter !== '' ? { alertType: typeFilter } : {}),
      limit: 50,
    }),
    [statusFilter, severityFilter, typeFilter],
  );

  const { data: alerts, isFetching } = useQuery({
    queryKey: ['aml-alerts', queryFilters],
    queryFn: async () => amlApi.listAlerts(queryFilters),
  });

  const { data: detail, isLoading: loadingDetail } = useQuery({
    queryKey: ['aml-alert', selected?.alertId],
    queryFn: async () =>
      selected !== null ? amlApi.getAlert(selected.alertId) : null,
    enabled: selected !== null,
  });

  const closeAction = (): void => {
    setActionKind(null);
    setAssigneeId('');
    setDismissReason('');
    setNarrative('');
  };

  const assignMutation = useMutation({
    mutationFn: async (input: { alertId: string; assigneeId: string }) =>
      amlApi.assign(input.alertId, input.assigneeId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['aml-alerts'] });
      await queryClient.invalidateQueries({ queryKey: ['aml-alert'] });
      closeAction();
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async (input: { alertId: string; reason: string }) =>
      amlApi.dismiss(input.alertId, input.reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['aml-alerts'] });
      await queryClient.invalidateQueries({ queryKey: ['aml-alert'] });
      closeAction();
      setSelected(null);
    },
  });

  const escalateMutation = useMutation({
    mutationFn: async (input: { alertId: string; narrative: string }) =>
      amlApi.escalate(input.alertId, input.narrative),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['aml-alerts'] });
      await queryClient.invalidateQueries({ queryKey: ['aml-alert'] });
      closeAction();
      setSelected(null);
    },
  });

  const submit = (): void => {
    if (selected === null || actionKind === null) return;
    if (actionKind === 'assign' && assigneeId.trim() !== '') {
      assignMutation.mutate({ alertId: selected.alertId, assigneeId: assigneeId.trim() });
    } else if (actionKind === 'dismiss' && dismissReason.trim() !== '') {
      dismissMutation.mutate({
        alertId: selected.alertId,
        reason: dismissReason.trim(),
      });
    } else if (actionKind === 'escalate' && narrative.trim() !== '') {
      escalateMutation.mutate({
        alertId: selected.alertId,
        narrative: narrative.trim(),
      });
    }
  };

  const items: AmlAlert[] = alerts ?? [];
  const detailData: AmlAlertDetail | null | undefined = detail;

  return (
    <>
      <Typography variant="h4" gutterBottom>
        AML Alert Queue
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AmlAlertStatus | '')}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Severity"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as AmlAlertSeverity | '')}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            {SEVERITY_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Alert type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as AmlAlertType | '')}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All</MenuItem>
            {TYPE_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Alert ID</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>SLA</TableCell>
              <TableCell>Assigned</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" py={2}>
                    No alerts
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => (
                <TableRow
                  key={row.alertId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelected(row)}
                >
                  <TableCell>
                    <code>{row.alertId}</code>
                  </TableCell>
                  <TableCell>{row.customerMaskedName}</TableCell>
                  <TableCell>{row.alertType}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.severity}
                      size="small"
                      color={severityColor(row.severity)}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusBadge kind="aml" value={row.status} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${String(row.daysOpen)}d`}
                      size="small"
                      color={agingColor(row.aging)}
                    />
                  </TableCell>
                  <TableCell>{row.assignedTo ?? '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Drawer
        anchor="right"
        open={selected !== null}
        onClose={() => setSelected(null)}
        PaperProps={{ sx: { width: 560, p: 3 } }}
      >
        {loadingDetail ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : detailData !== null && detailData !== undefined ? (
          <>
            <Typography variant="h5">{detailData.alertType}</Typography>
            <Typography variant="caption" color="text.secondary">
              <code>{detailData.alertId}</code>
            </Typography>

            <Stack direction="row" spacing={1} my={2} alignItems="center">
              <Chip label={detailData.severity} color={severityColor(detailData.severity)} />
              <StatusBadge kind="aml" value={detailData.status} />
              <Chip
                label={`${String(detailData.daysOpen)} days open`}
                color={agingColor(detailData.aging)}
              />
            </Stack>

            <Typography variant="subtitle2">Customer</Typography>
            <Typography sx={{ mb: 2 }}>
              {detailData.customerMaskedName} (<code>{detailData.customerId}</code>)
            </Typography>

            <Typography variant="subtitle2">Triggered rules</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              {detailData.triggeredRules.map((r) => (
                <Chip key={r} label={r} size="small" variant="outlined" />
              ))}
            </Stack>

            <Typography variant="subtitle2">Context</Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: 'grey.100',
                p: 1,
                borderRadius: 1,
                fontSize: 12,
                overflow: 'auto',
                mb: 2,
              }}
            >
              {JSON.stringify(detailData.context, null, 2)}
            </Box>

            <Typography variant="subtitle2">Related transactions</Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {detailData.relatedTransactions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              ) : (
                detailData.relatedTransactions.map((t) => (
                  <Box
                    key={t.id}
                    sx={{ p: 1, borderLeft: 3, borderColor: 'primary.main' }}
                  >
                    <Typography variant="body2">
                      <b>{t.type}</b> — {t.amountCents} {t.currency}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(t.occurredAt).toLocaleString(APP_LOCALE)}
                    </Typography>
                  </Box>
                ))
              )}
            </Stack>

            {detailData.sarCaseId !== null ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Escalated to SAR case: <code>{detailData.sarCaseId}</code>
              </Alert>
            ) : null}

            {isCompliance ? (
              <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                <Button variant="outlined" onClick={() => setActionKind('assign')}>
                  Assign
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => setActionKind('dismiss')}
                >
                  Dismiss
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setActionKind('escalate')}
                >
                  Escalate to SAR
                </Button>
              </Stack>
            ) : (
              <Alert severity="info" sx={{ mt: 3 }}>
                Read-only view. Compliance role required to act on alerts.
              </Alert>
            )}
          </>
        ) : null}
      </Drawer>

      <Dialog open={actionKind !== null} onClose={closeAction}>
        <DialogTitle>
          {actionKind === 'assign'
            ? 'Assign alert'
            : actionKind === 'dismiss'
              ? 'Dismiss alert'
              : 'Escalate to SAR'}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          {actionKind === 'assign' ? (
            <TextField
              autoFocus
              fullWidth
              label="Assignee user ID"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              sx={{ mt: 1 }}
            />
          ) : null}
          {actionKind === 'dismiss' ? (
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={4}
              label="Reason (required)"
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              sx={{ mt: 1 }}
            />
          ) : null}
          {actionKind === 'escalate' ? (
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={6}
              label="SAR narrative (required)"
              value={narrative}
              onChange={(e) => setNarrative(e.target.value)}
              sx={{ mt: 1 }}
              helperText="This will create a SAR draft. No tipping-off — do not notify the customer."
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              (actionKind === 'assign' && assigneeId.trim() === '') ||
              (actionKind === 'dismiss' && dismissReason.trim() === '') ||
              (actionKind === 'escalate' && narrative.trim() === '')
            }
            onClick={submit}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
